// ============================================================
// Barriere — une application n'affiche JAMAIS une page blanche.
// ------------------------------------------------------------
// Un import oublié dans un bloc de l'écran de chiffrage a effacé
// l'application entière : `#root` à zéro caractère, aucun message, rien
// à cliquer. L'utilisateur n'a même pas de quoi décrire ce qu'il voit.
//
// C'est le défaut de FOND, plus grave que le bug qui l'a déclenché : une
// erreur dans le bloc Temps ne doit pas emporter le plan, la matière et
// le prix. Ce qui casse s'isole ; le reste continue de servir.
//
// ── Pourquoi une barrière PAR BLOC et pas seulement une globale ──
//
// Une barrière globale transforme la page blanche en message d'erreur —
// c'est mieux, mais l'écran reste inutilisable. Une barrière par bloc
// laisse Charles travailler sur les huit blocs qui vont bien pendant que
// le neuvième affiche sa panne. Sur un devis de 22 pièces, la différence
// est entre « je ne peux rien faire » et « une pièce sur 22 est à
// reprendre ».
//
// ── L'identifiant d'incident ──
//
// Le même que celui du serveur. Sans lui, « ça a planté » n'est pas
// diagnosticable : on cherche dans les journaux d'une nuit entière au
// lieu d'une ligne.

import { Component } from 'react';
import Button from './Button.jsx';
import { api } from '../lib/api.js';

/** Un identifiant court, lisible au téléphone. */
function nouvelIncident() {
  const a = Math.random().toString(36).slice(2, 6).toUpperCase();
  const b = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `W-${a}-${b}`;
}

export class Barriere extends Component {
  constructor(props) {
    super(props);
    this.state = { erreur: null, incident: null };
  }

  static getDerivedStateFromError(erreur) {
    return { erreur, incident: nouvelIncident() };
  }

  componentDidCatch(erreur, info) {
    const { incident } = this.state;
    // Journalisé CÔTÉ SERVEUR avec le même identifiant : une erreur qui
    // ne vit que dans la console du navigateur de Charles est une
    // erreur qu'on ne verra jamais.
    api
      .post('/incidents', {
        incident_id: incident,
        zone: this.props.zone ?? 'application',
        message: String(erreur?.message ?? erreur).slice(0, 500),
        stack: String(erreur?.stack ?? '').slice(0, 4000),
        composant: String(info?.componentStack ?? '').slice(0, 2000),
        url: window.location.pathname,
      })
      .catch(() => {
        // Une barrière qui échoue à signaler ne doit pas re-planter :
        // elle a déjà une panne à afficher.
      });
  }

  render() {
    const { erreur, incident } = this.state;
    if (!erreur) return this.props.children;

    const global = !this.props.zone;
    return (
      <div className={global ? 'barriere barriere--globale' : 'barriere'} role="alert">
        <p className="barriere__titre">
          {global
            ? 'Un problème est survenu sur cet écran'
            : `Ce bloc n’a pas pu s’afficher${this.props.zone ? ` : ${this.props.zone}` : ''}`}
        </p>
        <p className="field__hint">
          {global
            ? 'Le reste de l’application fonctionne. Rien n’a été perdu : ce qui était enregistré l’est toujours.'
            : 'Les autres blocs de cette pièce continuent de fonctionner.'}
        </p>
        <p className="barriere__incident">
          Incident <strong>{incident}</strong> — communiquez-le pour qu’on le retrouve.
        </p>
        <div className="row">
          <Button variant="primary" onClick={() => window.location.reload()}>
            Recharger
          </Button>
          {global ? (
            <Button variant="secondary" onClick={() => { window.location.href = '/devis'; }}>
              Revenir à la liste des devis
            </Button>
          ) : (
            <Button variant="ghost" onClick={() => this.setState({ erreur: null, incident: null })}>
              Réessayer ce bloc
            </Button>
          )}
        </div>
      </div>
    );
  }
}

export default Barriere;
